import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { WorkOrderScheduleDto, ListWorkOrderScheduleDto, CreateWorkOrderScheduleDto, UpdateWorkOrderScheduleDto } from "../models/workorderschedule.models";

export const useGetWorkOrderSchedulesQuery = () => {
	const hasAccess = useAccess(AzureADRoles.Admin);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workOrderSchedules', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderScheduleDto[]>('/workorder-schedules');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
}

export const useGetWorkOrderScheduleByIdQuery = (scheduleId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.Admin);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workOrderSchedule', accountId, scheduleId],
		queryFn: async () => {
			let res = await api.get<WorkOrderScheduleDto>(`/workorder-schedules/${scheduleId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && scheduleId !== null && scheduleId !== "new" && scheduleId !== ""
	});
}

export const useGetWorkOrderSchedulesByTemplateQuery = (templateId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.Admin);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workOrderSchedulesByTemplate', accountId, templateId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderScheduleDto[]>(`/workorder-schedules/by-template/${templateId}`);
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && templateId !== null && templateId !== ""
	});
}

// export const useGetWorkOrderSchedulePreviewQuery = (scheduleId: string | null, count: number = 10) => {
// 	const hasAccess = useAccess(AzureADRoles.Admin);
// 	const accountId = useAccountId();

// 	return useQuery({
// 		queryKey: ['workOrderSchedulePreview', accountId, scheduleId, count],
// 		queryFn: async () => {
// 			let res = await api.get<{ executions: Date[] }>(`/workorder-schedules/${scheduleId}/preview-next?count=${count}`);
// 			return res.data.executions;
// 		},
// 		staleTime: 60 * 1000,
// 		gcTime: 5 * 60 * 1000,
// 		enabled: hasAccess && scheduleId !== null && scheduleId !== "new" && scheduleId !== ""
// 	});
// }

export const useCreateWorkOrderScheduleMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderScheduleDto>, Error, CreateWorkOrderScheduleDto>({
		mutationFn: (model) => {
			return api.post('/workorder-schedules', model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedules', accountId] });
		}
	});
}

export const useUpdateWorkOrderScheduleMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderScheduleDto>, Error, { id: string; model: UpdateWorkOrderScheduleDto }>({
		mutationFn: ({ id, model }) => {
			return api.put(`/workorder-schedules/${id}`, model);
		},
		onSuccess: (data, options) => {
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedule', accountId, options.id] });
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedulePreview', accountId, options.id] });
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedules', accountId] });
		}
	});
}

export const useDeleteWorkOrderScheduleMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<void>, Error, string>({
		mutationFn: (id) => {
			return api.delete(`/workorder-schedules/${id}`);
		},
		onSuccess: (_, scheduleId) => {
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedule', accountId, scheduleId] });
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedules', accountId] });
		}
	});
}

export const useActivateWorkOrderScheduleMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderScheduleDto>, Error, string>({
		mutationFn: (id) => {
			return api.post(`/workorder-schedules/${id}/activate`, {});
		},
		onSuccess: (data, scheduleId) => {
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedule', accountId, scheduleId] });
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedules', accountId] });
		}
	});
}

export const useDeactivateWorkOrderScheduleMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderScheduleDto>, Error, string>({
		mutationFn: (id) => {
			return api.post(`/workorder-schedules/${id}/deactivate`, {});
		},
		onSuccess: (data, scheduleId) => {
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedule', accountId, scheduleId] });
			queryClient.invalidateQueries({ queryKey: ['workOrderSchedules', accountId] });
		}
	});
}
